import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, XCircle, CalendarDays, CreditCard } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail, money } from "@/lib/api";

export default function BalancePay() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/balance/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const currency = info?.currency || "usd";

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/balance/${token}/pay`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10" data-testid="balance-pay-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>

        {loading && <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…</div>}

        {!loading && error && !info && (
          <div className="text-center" data-testid="balance-error">
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{error}</p>
          </div>
        )}

        {!loading && info && (
          <>
            <div className="text-center mb-6">
              <p className="text-primary font-medium text-xs uppercase tracking-widest mb-1">Complete your payment</p>
              <h1 className="font-serif text-2xl font-semibold text-foreground">Hi {info.guest_name}!</h1>
            </div>

            <div className="rounded-xl bg-accent/50 p-4 space-y-2 text-sm mb-5" data-testid="balance-summary">
              <div className="flex items-center gap-2 text-foreground mb-1"><CalendarDays className="h-4 w-4 text-primary" /> {info.check_in} → {info.check_out} · {info.nights} night(s)</div>
              <div className="flex justify-between text-muted-foreground"><span>Booking total</span><span className="text-foreground">{money(info.total, currency)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>Deposit already paid</span><span className="text-foreground">− {money(info.deposit_paid, currency)}</span></div>
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="text-foreground font-medium">Balance due now</span>
                <span className="text-primary font-serif text-2xl font-semibold" data-testid="balance-amount">{money(info.balance_due, currency)}</span>
              </div>
              {info.security_deposit_amount > 0 && (
                <p className="text-xs text-muted-foreground pt-1">Includes a {money(info.security_deposit_amount, currency)} refundable security deposit, returned after checkout if there's no damage.</p>
              )}
            </div>

            {info.balance_paid ? (
              <div className="text-center py-4" data-testid="balance-already-paid">
                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><Check className="h-7 w-7 text-primary" /></div>
                <p className="font-medium text-foreground">This balance is already paid.</p>
                <p className="text-sm text-muted-foreground mt-1">Thank you — we can't wait to host you!</p>
              </div>
            ) : (
              <>
                {error && <p className="text-sm text-destructive text-center mb-3" data-testid="balance-pay-error">{error}</p>}
                <Button className="w-full rounded-full bg-primary h-12 text-base" disabled={busy} data-testid="balance-pay-btn" onClick={pay}>
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-5 w-5 mr-1" /> Pay {money(info.balance_due, currency)}</>}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">Secure payment powered by Stripe.</p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
